import { FC, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { OrganizationRole, UpdateOrganizationSettingsBody } from "~/api/model";
import {
  useGetOrganization,
  useGetOrganizationSettings,
  useUpdateOrganizationSettings,
} from "~/api/organizations/organizations";
import logoImage from "~/assets/logo.svg";
import { useOrganizationParams } from "~/helpers";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
import logger from "~/utils/logger";
import { SettingsBranding } from "./SettingsBranding";
import { SettingsLang } from "./SettingsLang";
import { SettingsPreview } from "./SettingsPreview";
import { SettingsTypography } from "./SettingsTypography";

export const SettingsAppearance: FC = () => {
  useTrackPageVisit("app/settings_appearance/visit");
  const { mutateAsync: updateSettingsMutation } =
    useUpdateOrganizationSettings();
  const { organizationId } = useOrganizationParams();
  const { data: settings } = useGetOrganizationSettings(organizationId);
  const { data: organization } = useGetOrganization(organizationId);

  const [fontFamily, setFontFamily] = useState(settings?.fontFamily ?? "serif");
  const [lineSpacing, setLineSpacing] = useState(
    settings?.lineSpacing ?? "1.5"
  );
  const [logoUrl, setLogoUrl] = useState<string | null>(
    settings?.logoUrl ?? null
  );
  const [footnote, setFootnote] = useState<string | null>(
    (settings as any)?.footnote ?? null
  );

  const updateSettings = async (data: UpdateOrganizationSettingsBody) => {
    try {
      await updateSettingsMutation({
        organizationId,
        data,
      });
      if (data.fontFamily !== undefined) {
        setFontFamily(data.fontFamily ?? "serif");
      }
      if (data.lineSpacing !== undefined) {
        setLineSpacing(data.lineSpacing ?? "1.5");
      }
      if (data.logoS3Key === null) setLogoUrl(null);
      if (data.footnote !== undefined) {
        setFootnote(data.footnote);
      }
    } catch (error) {
      logger.error("Failed to update organization settings:", error);
    }
  };

  useEffect(() => {
    setFontFamily(settings?.fontFamily ?? "serif");
    setLineSpacing(settings?.lineSpacing ?? "1.5");
    setLogoUrl(settings?.logoUrl ?? null);
    setFootnote((settings as any)?.footnote ?? null);
  }, [
    settings?.fontFamily,
    settings?.lineSpacing,
    settings?.logoUrl,
    settings,
  ]);

  const previewLogo = logoUrl ?? logoImage;
  const canManageAppearance =
    organization?.role === OrganizationRole.OWNER ||
    organization?.role === OrganizationRole.SUPERADMIN;

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-14 w-full">
      <div className="flex flex-col gap-6 mt-2 w-full lg:w-[60%]">
        <div className="flex flex-col gap-2">
          <h3 className="text-2xl font-semibold">
            <FormattedMessage
              id="settings.appearance"
              defaultMessage={"Appearance"}
            />
          </h3>
          <p className="text-sm text-muted-foreground">
            <FormattedMessage
              id="settings.typography"
              defaultMessage={
                "Customize the appearance of your exported reports and documents. These settings will be applied to all PDF and DOCX exports"
              }
            />
          </p>
        </div>
        <SettingsLang />
        {canManageAppearance && (
          <>
            <div className="border-t pt-4">
              <h4 className="text-lg font-semibold mb-4">
                <FormattedMessage
                  id="settings.typographyTitle"
                  defaultMessage={"Typography"}
                />
              </h4>

              {settings && organization && (
                <SettingsTypography
                  updateSettings={updateSettings}
                  settings={settings}
                  organization={organization}
                />
              )}
            </div>

            <div className="border-t pt-4">
              <h4 className="text-lg font-semibold mb-4">
                <FormattedMessage
                  id="settings.branding"
                  defaultMessage={"Branding Assets"}
                />
              </h4>
              <SettingsBranding
                updateSettings={updateSettings}
                logoUrl={logoUrl}
                footnote={footnote}
              />
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-4 mt-2 w-full lg:w-[60%] lg:max-w-[400px]">
        <div>
          <h4 className="text-lg font-semibold mb-2">
            <FormattedMessage
              id="settings.preview"
              defaultMessage={"Preview"}
            />
          </h4>
          <p className="text-sm text-muted-foreground">
            <FormattedMessage
              id="settings.preview.description"
              defaultMessage={
                "See how your settings will appear in exported documents"
              }
            />
          </p>
        </div>
        <SettingsPreview
          fontFamily={fontFamily}
          lineSpacing={lineSpacing}
          logo={previewLogo}
          footnote={footnote}
        />
      </div>
    </div>
  );
};
